import React, { useEffect, useRef } from 'react';
import { Grid } from '@mui/material';
import { Button, Stack, TextField } from '@curves/ui-base';
import { Overview } from '@curves/ui-shared';

import { CompanyDialogBox } from '@Local/ClubCompany/Dialog';
import { PageLayout } from '@Local/Components/Layout';
import useCompanyApi from '@Apis/useCompanyApi';
import useOverview from '@Util/hook/useOverview';

const defaultSearchParams = {
    pageIndex: 1,
    pageSize: 20,
};

/**
 * 公司管理頁
 */
const CompanyPage = () => {
    const { postCompaniesOverviewApi, getCompanyApi } = useCompanyApi();
    const dialogRef = useRef(null);
    const companyNameRef = useRef(null);
    const taxIDRef = useRef(null);
    const { overviewData, getOverviewData, searchParams } = useOverview({
        getOverviewApi: postCompaniesOverviewApi,
        defaultSearchParams,
        isFetchOnMount: true,
        enableQueryString: true,
    });

    // Dialog 非同步完成後仍需沿用使用者最後一次的搜尋條件，因此以 ref 避免 callback 捕捉到舊 state。
    const searchParamsRef = useRef(searchParams);

    useEffect(() => {
        searchParamsRef.current = searchParams;
    }, [searchParams]);

    const handleGetOverviewData = params => {
        // 先同步 ref，讓新增／編輯成功後的 refresh 可立即用同一組條件重查。
        searchParamsRef.current = params;
        return getOverviewData(params);
    };

    const refreshCompanies = () => handleGetOverviewData(searchParamsRef.current);

    const handlePageChange = (e, page) => {
        // 換頁只更新頁碼，保留目前所有搜尋條件。
        handleGetOverviewData({ ...searchParamsRef.current, pageIndex: page });
    };

    const getSearchParams = () => {
        const companyName = (companyNameRef.current?.getResult?.() ?? '').trim();
        const taxID = (taxIDRef.current?.getResult?.() ?? '').trim();

        return {
            companyName,
            taxID,
            pageIndex: 1,
            pageSize: 20,
        };
    };

    const contextValue = {
        overviewData,
        searchParams,
        getOverviewData: handleGetOverviewData,
        updateOverviewPage: page => handlePageChange(null, page),
    };

    const handleOpenCompanyDialog = async (targetData = null) => {
        let sourceData = targetData;

        if (targetData?.companyID) {
            sourceData = await getCompanyApi(targetData.companyID);
        }

        if (dialogRef.current && dialogRef.current.openCompanyDialog) {
            dialogRef.current.openCompanyDialog(sourceData, refreshCompanies);
        }
    };

    const pageActions = [
        {
            label: '新增公司',
            variant: 'contained',
            onClick: () => handleOpenCompanyDialog(null),
        },
    ];

    const headerRow = [
            { cellKey: 'companyName', headerLabel: '公司名稱' },
            { cellKey: 'taxID', headerLabel: '統編' },
            { cellKey: 'taxTitle', headerLabel: '抬頭' },
            { cellKey: 'companyNumber', headerLabel: '電話' },
            {
                cellKey: 'action',
                headerLabel: '操作',
                align: 'center',
                formatCell: (_, rowData) => (
                    <Stack direction={'row'} spacing={1} justifyContent={'center'}>
                        <Button variant={'outlined'} size={'small'} color="primary" onClick={() => handleOpenCompanyDialog(rowData)}>
                            編輯
                        </Button>
                    </Stack>
                ),
            },
    ];

    return (
        <PageLayout className={'company-container'}>
            <PageLayout.Header title="公司管理" actions={pageActions} />
            <Overview contextValue={contextValue} idKey="companyID">
                <Overview.Search className="p-3 mb-1" getSearchParamsOnSubmit={getSearchParams}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                key={searchParams?.companyName || ''}
                                ref={companyNameRef}
                                label={'公司名稱'}
                                defaultValue={searchParams?.companyName || ''}
                                InputProps={{ placeholder: '請輸入公司名稱' }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                key={searchParams?.taxID || ''}
                                ref={taxIDRef}
                                label={'統編'}
                                defaultValue={searchParams?.taxID || ''}
                                InputProps={{ placeholder: '請輸入統一編號' }}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                    <Stack className={'mt-2'} justifyContent={'end'} spacing={2}>
                        <Button variant={'contained'} color="primary" type="submit">
                            搜尋
                        </Button>
                    </Stack>
                </Overview.Search>
                <Stack className={'overview__toolbar'} alignItems={'center'} justifyContent={'end'}>
                    <Overview.TotalCount showRange />
                </Stack>
                <Overview.Table headerRow={headerRow} />
                <Overview.Pagination className="mt-4" />
            </Overview>
            <CompanyDialogBox ref={dialogRef} />
        </PageLayout>
    );
};

export default CompanyPage;
